import { useState } from 'react';
import { Description, ExpandMore } from '@mui/icons-material';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Box
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { createCabinetLayoutDownload } from '../../../fetch';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { Form, Formik } from 'formik';
import { downloadFile, getDownloadFileName } from '../../../helpers';
import { SalesPlansDropdown } from '../../../components/dropdowns/sales-plans-dropdown';
import { SalesOvenLayoutDropdown } from '../../../components/dropdowns/sales-oven-layout-dropdown';

export const PlanCabinetLayout = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isGeneratingDoc, setIsGeneratingDoc] = useState<boolean>(false);

  const handleDownload = async (plan: number, ovenLayout: string) => {
    try {
      setIsGeneratingDoc(true);
      const res = await createCabinetLayoutDownload(plan, ovenLayout);
      return downloadFile(res.data, getDownloadFileName(res));
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error, unable to download file, please try again.`, {
        variant: 'error'
      });
      console.log({ error });
    } finally {
      setIsGeneratingDoc(false);
    }
  };

  return (
    <>
      <Accordion className={classes.root}>
        <AccordionSummary
          className={classes.accordion}
          expandIcon={<ExpandMore />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <div className={classes.topPanelSummaryWrapper}>
            <Typography variant="h6">Plan Cabinet Layout</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <div className={classes.panelSummaryWrapper}>
            <Box mt={1}>
              <Formik
                enableReinitialize={true}
                initialValues={{
                  plans: '',
                  ovenLayout: ''
                }}
                onSubmit={() => {}}
              >
                {({
                  isSubmitting,
                  values,
                  initialValues,
                  setFieldValue,
                  handleSubmit,
                  dirty,
                  isValid,
                  handleBlur,
                  errors,
                  touched
                }) => {
                  return (
                    <>
                      <Form onSubmit={() => handleSubmit} autoComplete="none">
                        <Grid container spacing={1} flexWrap={{ xs: 'wrap', xl: 'nowrap' }}>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <SalesPlansDropdown
                              isRequired={true}
                              isDisabled={isGeneratingDoc}
                              value={values.plans}
                              handleChange={val => {
                                setFieldValue('plans', val);
                                setFieldValue('ovenLayout', '');
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <SalesOvenLayoutDropdown
                              isRequired={true}
                              isDisabled={!values.plans || isGeneratingDoc}
                              value={values.ovenLayout}
                              handleChange={val => {
                                setFieldValue('ovenLayout', val);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={'auto'}>
                            <LoadingButton
                              id="generate-cabinet-layout-doc"
                              type="button"
                              color="primary"
                              variant="contained"
                              disabled={!values.ovenLayout}
                              loading={isGeneratingDoc}
                              startIcon={<Description />}
                              className={classes.generateBtn}
                              onClick={() =>
                                handleDownload(Number(values.plans), values.ovenLayout)
                              }
                            >
                              Generate & Download
                            </LoadingButton>
                          </Grid>
                        </Grid>
                      </Form>
                    </>
                  );
                }}
              </Formik>
            </Box>
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  generateBtn: {
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  }
}));
